import { Loading, ToastProvider } from '@iheartjane/reefer';
import { Suspense, useEffect } from 'react';
import type { PropsWithChildren } from 'react';
import type { RouteObject } from 'react-router';
import { Navigate, Outlet } from 'react-router';

import {
  Authenticated,
  RumErrorBoundary as ErrorBoundary,
  ErrorView,
  GlobalSpecialsModal,
  IdentifyManager,
  NotFoundPage,
  RequireLoggedInUser,
  SquareOAuth,
  UnauthorizedAccess,
} from '@jane/business-admin/components';
import {
  useAuthenticated,
  useHasPermissions,
  useIsFulfillmentV2Enabled,
  useOnlyFulfillmentsPermission,
} from '@jane/business-admin/hooks';
import { Permission } from '@jane/shared/auth';
import { load } from '@jane/shared/util';

import { analyticsRoutes } from './routing/analyticsRoutes';
import { authRoutes } from './routing/authRoutes';
import { premiumRoutes } from './routing/premiumRoutes';
import { storeRoutes } from './routing/storeRoutes';

const BusinessSolutions = load(() => import('./features/BusinessSolutions'));
const TermsOfService = load(() => import('./features/TermsOfService'));
const PrivacyPolicy = load(() => import('./features/PrivacyPolicy'));
const GlobalSpecials = load(() => import('./features/GlobalSpecials'));
const BoostMenus = load(() => import('./features/BoostMenus'));

const Shell = load(() => import('./features/Shell'));

const PublicRoot = () => (
  <Authenticated>
    <IdentifyManager />
    <ToastProvider verticalOffset={96} usePortal>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </ToastProvider>
  </Authenticated>
);

// Full-page navigation to the fulfillment Rails UI. <Navigate> would only
// update SPA history and never load the server-rendered page; the existing
// warden session cookie (set at manager login) flows along with the
// browser-level navigation.
const FulfillmentRedirect = () => {
  useEffect(() => {
    window.location.replace('/web/fulfillment');
  }, []);
  return <Loading />;
};

const GatedRoot = ({ children }: PropsWithChildren) => {
  const isOnlyFulfillments = useOnlyFulfillmentsPermission();
  const isFulfillmentV2Enabled = useIsFulfillmentV2Enabled();

  if (isOnlyFulfillments === undefined) {
    return <Loading />;
  }

  if (isOnlyFulfillments && isFulfillmentV2Enabled) {
    return <FulfillmentRedirect />;
  }

  return (
    <RequireLoggedInUser>
      <Shell>
        <ErrorBoundary fallback={ErrorView} scope="authenticated">
          <Outlet />
          {children}
        </ErrorBoundary>
      </Shell>
    </RequireLoggedInUser>
  );
};

const FallbackRoute = () => {
  const isLoggedIn = useAuthenticated();

  return isLoggedIn ? (
    <GatedRoot>
      <NotFoundPage />
    </GatedRoot>
  ) : (
    <Navigate to="/login" replace />
  );
};

const GatedBoostMenuRoute = () => {
  const isAllowed = useHasPermissions([Permission.EditEmbedConfigs]);

  if (isAllowed === undefined) return null;

  return !isAllowed ? (
    <UnauthorizedAccess resourceName="boost menus" />
  ) : (
    <BoostMenus />
  );
};

export const createRoutes = (): RouteObject[] => [
  {
    element: <PublicRoot />,
    errorElement: <NotFoundPage />,
    children: [
      /** Unauthenticated routes */
      {
        path: '/',
        element: <Navigate to="/login" replace />,
      },
      {
        path: '*',
        element: <FallbackRoute />,
      },
      ...authRoutes(),
      {
        path: '/privacy-policy',
        element: <PrivacyPolicy />,
      },
      {
        path: '/square-oauth',
        element: <SquareOAuth />,
      },
      {
        path: '/terms',
        element: <TermsOfService />,
      },
      /** Authenticated routes */
      {
        element: <GatedRoot />,
        children: [
          ...analyticsRoutes(),
          {
            path: '/boost',
            element: <GatedBoostMenuRoute />,
          },
          {
            path: '/marketing',
            children: [
              {
                path: 'business-solutions',
                element: <BusinessSolutions />,
              },
              {
                path: '/marketing/',
                element: <Navigate to="business-solutions" replace={true} />,
              },
            ],
          },
          ...premiumRoutes(),
          {
            path: '/specials',
            element: <GlobalSpecials />,
            children: [
              {
                path: ':special_id',
                element: <GlobalSpecialsModal />,
              },
              {
                path: 'duplicate/:special_id',
                element: <GlobalSpecialsModal />,
              },
            ],
          },
          ...storeRoutes(),
        ],
      },
    ],
  },
];
